/**
 * Renders the main admin app, which includes the navigation menu including the routes.
 * The content shown when clicking at the menu items is referred to as 'Sections'
 */

import React, {useContext} from "react";
import {BrowserRouter as Router, Switch} from 'react-router-dom'
import clsx from 'clsx';
import Drawer from '@mui/material/Drawer';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import Container from '@mui/material/Container';

import {useStyles} from 'components/styles/App.css'
import VideosSection from 'components/admin/sections/videos/overview/VideosSection';
import StatisticsSection from 'components/admin/sections/statistics/overview/StatisticsSection';
import UsersSection from 'components/admin/sections/users/overview/UsersSection';
import AboutSection from 'components/commons/sections/about/AboutSection';
import HelpSection from 'components/commons/sections/help/HelpSection';
import AccountInfo from 'components/commons/account/AccountInfo';
import ProfileOverviewSection from 'components/commons/sections/profile/overview/ProfileOverviewSection';
import AdminNavItemsPrimary from 'components/admin/navigation/navItems/AdminNavItemsPrimary';
import AdminNavItemsSecondary from 'components/admin/navigation/navItems/AdminNavItemsSecondary';
import CurrentAppbarContent from 'components/commons/routing/appbar/CurrentAppbarContent';
import EditUserSection from "components/admin/sections/users/edit/EditUserSection";
import PrivateRoute from "components/commons/routing/routes/PrivateRoute";
import NewUserSection from "../../sections/users/new/NewUserSection";
import ChangePasswordSection from "../../../commons/sections/profile/changePassword/ChangePasswordSection";
import EditProfileSection from "../../../commons/sections/profile/edit/EditProfileSection";
import EditVideoSection from "../../sections/videos/edit/EditVideoSection";
import NewVideoSection from "../../sections/videos/new/NewVideoSection";
import NewScoredSceneSection from "../../sections/videosScored/new/NewScoredSceneSection";
import EditScoredSceneSection from "../../sections/videosScored/edit/EditScoredSceneSection";
import NewPlaylistSection from "../../sections/playlists/new/NewPlaylistSection";
import PlaylistsSection from "../../sections/playlists/overview/PlaylistsSection";
import EditPlaylistSection from "../../sections/playlists/edit/EditPlaylistSection";
import NewCourseSection from "../../sections/courses/new/NewCourseSection";
import CoursesSection from "../../sections/courses/overview/CoursesSection";
import EditCourseSection from "../../sections/courses/edit/EditCourseSection";
import ChallengedScoredScenesSection
    from "../../sections/challengedDecisions/challengedScoredScenesOverview/ChallengedScoredScenesSection";
import {AppbarContentContext} from "../../../commons/routing/appbar/AppbarContentProvider";
import DashboardGate from "../../sections/dashboard/gate/DashboardGate";

function AdminApp() {
    // Styles for the general administration interface
    const classes = useStyles();

    // Open and closing of the navigation drawer on the very left side
    const [open, setOpen] = React.useState(true);

    // For updating the current drawer open state (open vs. closed), so that other components can react on it
    const {setIsDrawerOpen} = useContext(AppbarContentContext)

    const handleDrawerOpen = () => {
        setOpen(true);
        setIsDrawerOpen(true)
    };
    const handleDrawerClose = () => {
        setOpen(false);
        setIsDrawerOpen(false)
    };

    // Main component for the admin interface - also includes the admin-specific routes
    return (
        <Router>
            <div className={classes.root}>

                {/* Visualization and handling of the header bar */}
                <AppBar position="absolute" className={clsx(classes.appBar, open && classes.appBarShift)}>
                    <Toolbar className={classes.toolbar}>
                        <IconButton
                            edge="start"
                            color="inherit"
                            aria-label="open drawer"
                            onClick={handleDrawerOpen}
                            className={clsx(classes.menuButton, open && classes.menuButtonHidden)}
                            size="large">
                            <MenuIcon/>
                        </IconButton>
                        <Typography component="h1" variant="h6" color="inherit" noWrap
                                    className={classes.title}>
                            <CurrentAppbarContent/>
                        </Typography>
                        <AccountInfo/>
                    </Toolbar>
                </AppBar>

                {/* Visualization and handling of the drawer including the navigation items */}
                <Drawer variant="permanent"
                        classes={{
                            paper: clsx(classes.drawerPaper, !open && classes.drawerPaperClose),
                        }}
                        open={open}
                >
                    <div className={classes.toolbarIcon}>
                        <IconButton onClick={handleDrawerClose} size="large">
                            <ChevronLeftIcon/>
                        </IconButton>
                    </div>
                    <Divider/>
                    <AdminNavItemsPrimary/>
                    <Divider/>
                    <AdminNavItemsSecondary/>
                </Drawer>

                {/* Visualization and handling of the routes leading to the respective content sections */}
                <main className={classes.content}>
                    <div className={classes.appBarSpacer}/>
                    <Container maxWidth={false} className={classes.container}>
                        {/* Routes navigation */}
                        <Switch>
                            {/* Primary navigation items */}

                            {/* Dashboard */}
                            <PrivateRoute exact path={'/admin/dashboard'}
                                          allowedRoles={['admin', 'contentCreator', 'organizer']}
                                          component={DashboardGate}/>

                            {/* Scored Scenes */}
                            <PrivateRoute path={'/admin/videos/:videoKey/scoredScenes/new'}
                                          allowedRoles={['admin', 'contentCreator', 'organizer']}
                                          component={NewScoredSceneSection}/>
                            <PrivateRoute path={'/admin/videos/:videoKey/scoredScenes/:scoredSceneKey/edit'}
                                          allowedRoles={['admin', 'contentCreator', 'organizer']}
                                          component={EditScoredSceneSection}/>

                            {/* Videos */}
                            <PrivateRoute exact path={'/admin/videos'}
                                          allowedRoles={['admin', 'contentCreator', 'organizer']}
                                          component={VideosSection}/>
                            <PrivateRoute path={'/admin/videos/new'}
                                          allowedRoles={['admin', 'contentCreator', 'organizer']}
                                          component={NewVideoSection}/>
                            <PrivateRoute path={'/admin/videos/:key/edit'}
                                          allowedRoles={['admin', 'contentCreator', 'organizer']}
                                          component={EditVideoSection}/>

                            {/* Playlists */}
                            <PrivateRoute path={'/admin/playlists/new'}
                                          allowedRoles={['admin', 'contentCreator', 'organizer']}
                                          component={NewPlaylistSection}/>
                            <PrivateRoute path={'/admin/playlists/:key/edit'}
                                          allowedRoles={['admin', 'contentCreator', 'organizer']}
                                          component={EditPlaylistSection}/>
                            <PrivateRoute path={'/admin/playlists'}
                                          allowedRoles={['admin', 'contentCreator', 'organizer']}
                                          component={PlaylistsSection}/>

                            {/* Courses */}
                            <PrivateRoute path={'/admin/courses/:key/edit'} allowedRoles={['admin', 'organizer']}
                                          component={EditCourseSection}/>
                            <PrivateRoute path={'/admin/courses/new'} allowedRoles={['admin', 'organizer']}
                                          component={NewCourseSection}/>
                            <PrivateRoute path={'/admin/courses'} allowedRoles={['admin', 'organizer']}
                                          component={CoursesSection}/>

                            {/* Users */}
                            <PrivateRoute exact path={'/admin/users'} allowedRoles={['admin', 'organizer']}
                                          component={UsersSection}/>
                            <PrivateRoute path={'/admin/users/:uid/edit'} allowedRoles={['admin', 'organizer']}
                                          component={EditUserSection}/>
                            <PrivateRoute path={'/admin/users/new'} allowedRoles={['admin', 'organizer']}
                                          component={NewUserSection}/>

                            {/* Challenged Decisions */}
                            <PrivateRoute exact path={'/admin/challengedDecisions'}
                                          allowedRoles={['admin', 'contentCreator', 'organizer']}
                                          component={ChallengedScoredScenesSection}/>

                            {/* Statistics */}
                            <PrivateRoute path={'/admin/statistics'} allowedRoles={['admin', 'organizer']}
                                          component={StatisticsSection}/>

                            {/* Profile */}
                            <PrivateRoute exact path={'/common/profile'}
                                          allowedRoles={['admin', 'contentCreator', 'organizer']}
                                          component={ProfileOverviewSection}/>
                            <PrivateRoute path={'/common/profile/edit'}
                                          allowedRoles={['admin', 'contentCreator', 'organizer']}
                                          component={EditProfileSection}/>
                            <PrivateRoute path={'/common/profile/changePassword'}
                                          allowedRoles={['admin', 'contentCreator', 'organizer']}
                                          component={ChangePasswordSection}/>

                            {/* Secondary navigation items */}
                            <PrivateRoute path={'/common/about'} allowedRoles={['admin', 'contentCreator', 'organizer']}
                                          component={AboutSection}
                            />
                            <PrivateRoute path={'/common/help'} allowedRoles={['admin', 'contentCreator', 'organizer']}
                                          component={HelpSection}/>
                        </Switch>
                    </Container>
                </main>
            </div>
        </Router>
    );
}

export default AdminApp;